import type { ClassificationOutputType } from '../classification/ClassificationService';

/** Visual emphasis for a verdict, derived from its magnitude. */
export type TurnTone = 'danger' | 'caution' | 'neutral';

/** One AI turn classified under a policy, from the OLAP interactions layer. */
export interface ClassifiedItem {
    id: string;
    sessionId: string;
    turnIndex: number;
    timestampIso: string;
    person: string;
    department: string;
    /** The verdict value, formatted for the policy's output type. */
    output: string;
    /** Numeric verdict for scale/currency/percent; null otherwise. */
    valueNum: number | null;
    tone: TurnTone;
    /** Classifier confidence in [0,1], or null. */
    confidence: number | null;
}

function numberConfig(config: Record<string, unknown>, key: string, fallback: number): number {
    const value = config[key];
    return typeof value === 'number' ? value : fallback;
}

/** Inclusive bounds of a scale policy. One shared resolver so display, tone, and severity agree. */
export interface ScaleBounds {
    min: number;
    max: number;
}

/**
 * Resolve a scale policy's [min, max]. Defaults match the classification builder
 * (`ClassificationOptions`): min=1, max=10. Historically `formatVerdict` defaulted max to
 * 0 while tone/severity defaulted to 10 — that split let out-of-range values render as
 * "94015 / 5" and score as critical. This is the single source of truth.
 */
export function scaleBounds(config: Record<string, unknown>): ScaleBounds {
    const min = numberConfig(config, 'min', 1);
    const max = numberConfig(config, 'max', 10);
    return max > min ? { min, max } : { min, max: min + 1 };
}

/** Whether a numeric verdict falls within its scale's configured bounds. */
export function inScale(valueNum: number, bounds: ScaleBounds): boolean {
    return Number.isFinite(valueNum) && valueNum >= bounds.min && valueNum <= bounds.max;
}

function formatCurrency(amount: number, code: string): string {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(amount);
    } catch {
        return `${code} ${Math.round(amount).toLocaleString('en-US')}`;
    }
}

/** Tone for a verdict: high numeric magnitudes read as concern; category/text are neutral. */
export function verdictTone(outputType: ClassificationOutputType, valueNum: number | null, outputConfig: Record<string, unknown>): TurnTone {
    if (valueNum === null) return 'neutral';
    if (outputType === 'scale') {
        const bounds = scaleBounds(outputConfig);
        if (!inScale(valueNum, bounds)) return 'caution';
        const frac = (valueNum - bounds.min) / (bounds.max - bounds.min);
        return frac >= 0.66 ? 'danger' : frac >= 0.33 ? 'caution' : 'neutral';
    }
    if (outputType === 'percent') {
        return valueNum >= 75 ? 'danger' : valueNum >= 40 ? 'caution' : 'neutral';
    }
    if (outputType === 'currency') {
        return valueNum >= 2500 ? 'caution' : 'neutral';
    }
    return 'neutral';
}

/**
 * Category verdict values that read as a governance concern. Heuristic (not per-policy
 * config): compliance/security language. Numeric/scale concern is magnitude-based instead.
 */
const CATEGORY_CONCERN = /non.?compliant|violation|breach|fail|denied|reject|unapproved|prohibit|escalat|critical|severe|sensitive|restricted|confidential|leak|exfiltrat|personal|shadow|unsafe/i;

/**
 * Whether a verdict value is a concern for its policy: high-end magnitude for
 * scale/percent (top third / ≥75%), or compliance/security language for category.
 * Currency and free-text are not auto-flagged.
 */
export function isConcerningValue(outputType: ClassificationOutputType, value: string, outputConfig: Record<string, unknown>): boolean {
    if (outputType === 'scale') {
        const n = Number(value);
        if (!Number.isFinite(n)) return false;
        const bounds = scaleBounds(outputConfig);
        if (!inScale(n, bounds)) return true;
        return n >= bounds.min + 0.66 * (bounds.max - bounds.min);
    }
    if (outputType === 'percent') {
        const n = Number(value);
        return Number.isFinite(n) && n >= 75;
    }
    if (outputType === 'category') return CATEGORY_CONCERN.test(value);
    return false;
}

const SEVERE_CATEGORY = /breach|violation|critical|non.?compliant|leak|exfiltrat|prohibit|severe|unsafe/i;

/** Severity of a concerning verdict: near the top of a numeric range, or hard-violation language, reads as critical. */
export function concernSeverity(outputType: ClassificationOutputType, value: string, outputConfig: Record<string, unknown>): 'critical' | 'high' | 'medium' {
    if (outputType === 'scale') {
        const n = Number(value);
        if (!Number.isFinite(n)) return 'high';
        const bounds = scaleBounds(outputConfig);
        if (!inScale(n, bounds)) return 'high';
        const frac = (n - bounds.min) / (bounds.max - bounds.min);
        return frac >= 0.9 ? 'critical' : 'high';
    }
    if (outputType === 'percent') {
        const n = Number(value);
        return Number.isFinite(n) && n >= 90 ? 'critical' : 'high';
    }
    if (outputType === 'category') return SEVERE_CATEGORY.test(value) ? 'critical' : 'high';
    return 'medium';
}

/** A verdict value formatted for display per output type (e.g. "7 / 10", "82%", "$3,200"). */
export function formatVerdict(outputType: ClassificationOutputType, value: string, valueNum: number | null, outputConfig: Record<string, unknown>): string {
    if (valueNum === null) return value;
    if (outputType === 'percent') return `${Math.round(valueNum)}%`;
    if (outputType === 'currency') {
        const code = typeof outputConfig.currencyCode === 'string' ? outputConfig.currencyCode : 'USD';
        return formatCurrency(valueNum, code);
    }
    if (outputType === 'scale') {
        const bounds = scaleBounds(outputConfig);
        if (!inScale(valueNum, bounds)) return `invalid (${valueNum.toLocaleString()})`;
        return `${valueNum} / ${bounds.max}`;
    }
    return value;
}
